// Entrées de navigation de l'espace Administration (charte « Séance de décision ») : entrées
// principales de l'en-tête (Décisions, Pilotage, Courriers, Dossiers, Finances, Territoire,
// Conseil) et entrées du menu « Plus ». Filtrées par les droits du contexte actif et par les
// modules activés (H9-01 : un module désactivé masque ses écrans de navigation). Une entrée
// n'apparaît que si son écran existe réellement.
import type { EntreeNavigation } from '@eec/ui';
import {
  CalendarDays,
  Coins,
  FileCog,
  FileText,
  FolderOpen,
  Gavel,
  History,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  Map as IconeCarte,
  Mail,
  MessageSquareWarning,
  Search,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Smartphone,
  Handshake,
  Store,
  Users,
  Wrench,
  Recycle,
  ClipboardCheck,
  Landmark,
  TriangleAlert,
  MessageSquare,
  Package,
} from 'lucide-react';
import type { ContexteActif, ModuleEtat } from '../api/types';
import { peut, rolesActifs, voitDomaine } from '../session/permissions';

interface DefinitionEntree extends Omit<EntreeNavigation, 'badge'> {
  visible: (c: ContexteActif | null) => boolean;
  module?: string;
  /** Entrée du menu « Plus » plutôt que de l'en-tête. */
  secondaire?: boolean;
}

/** Le super administrateur de la plateforme (D-080) ne voit que ses écrans et ses préférences. */
const horsPlateforme = (c: ContexteActif | null) => c?.appartenance_active?.institution !== 'plateforme';

const DEFINITIONS: DefinitionEntree[] = [
  { id: 'controles-regulation', libelle: 'Contrôles de régulation', href: '/controles-regulation', icone: Landmark, visible: c => c?.appartenance_active?.institution === 'regulateur' && peut(c,'regulation','voir'), secondaire:true },
  { id:'reclamations-reseau',libelle:'Réclamations',href:'/reclamations-reseau',icone:MessageSquareWarning,visible:c=>peut(c,'reclamation','voir'),secondaire:true },
  {id:'terrain-reseau',libelle:'Terrain hors connexion',href:'/terrain',icone:Smartphone,visible:c=>peut(c,'intervention','creer'),secondaire:true},
  { id:'communication-reseau',libelle:'Communication',href:'/communication-reseau',icone:Mail,visible:c=>peut(c,'communication','voir'),secondaire:true },
  { id: 'investissements-reseau', libelle: 'Investissements', href: '/investissements-reseau', icone: Landmark, visible: c => !c?.appartenance_active?.institution && peut(c,'investissement','voir'), secondaire:true },
  { id: 'instruction-investissements', libelle: 'Suivi des investissements', href: '/instruction-investissements', icone: Landmark, visible: c => peut(c,'investissement','voir'), secondaire:true },
  { id: 'mandats-institutionnels', libelle: 'Mandats institutionnels', href: '/mandats-institutionnels', icone: KeyRound, visible: c => c?.appartenance_active?.institution === 'ministere' && peut(c,'utilisateurs','voir'), secondaire:true },
  { id: 'couts-reseau', libelle: 'Coûts', href: '/couts-reseau', icone: Coins, visible: c => peut(c,'cout','voir'), secondaire:true },
  { id: 'pilotage-reseaux', libelle: 'Pilotage des réseaux', href: '/pilotage-reseaux', icone: LayoutDashboard, visible: c => ['ministere','regulateur','coordination_provinciale'].includes(c?.appartenance_active?.institution??'') },
  { id: 'notifications-reseau', libelle: 'Notifications', href: '/notifications-reseau', icone: Mail, visible: c => peut(c,'intervention','voir'), secondaire:true },
  { id: 'equipes-reseau', libelle: 'Équipes', href: '/equipes-reseau', icone: Users, visible: c => peut(c,'intervention','modifier'), secondaire:true },
  { id: 'decisions', libelle: 'Décisions', href: '/decisions', icone: Gavel, visible: (c) => voitDomaine(c, 'dossier') || voitDomaine(c, 'courrier'), module: 'M02' },
  { id: 'pilotage', libelle: 'Accueil', href: '/', icone: LayoutDashboard, visible: () => true },
  { id: 'incidents-reseau', libelle: 'Incidents', href: '/incidents', icone: TriangleAlert, visible: (c) => peut(c, 'incident', 'voir') },
  { id: 'signalements-reseau', libelle: 'Signalements', href: '/signalements', icone: MessageSquare, visible: (c) => peut(c, 'signalement', 'voir') },
  { id: 'missions-reseau', libelle: 'Interventions', href: '/interventions-reseau', icone: Wrench, visible: (c) => peut(c, 'intervention', 'voir') },
  { id: 'stocks-reseau', libelle: 'Stocks', href: '/stocks', icone: Package, visible: (c) => peut(c, 'stock', 'voir'), secondaire: true },
  { id: 'patrimoine-reseau', libelle: 'Patrimoine', href: '/patrimoine', icone: IconeCarte, visible: (c) => peut(c, 'patrimoine', 'voir'), secondaire: true },
  { id: 'maintenance-reseau', libelle: 'Maintenance', href: '/maintenance', icone: CalendarDays, visible: (c) => peut(c, 'maintenance', 'voir'), secondaire: true },
  { id: 'courriers', libelle: 'Courriers', href: '/courriers', icone: Mail, visible: (c) => voitDomaine(c, 'courrier'), module: 'M02' },
  { id: 'dossiers', libelle: 'Dossiers', href: '/dossiers', icone: FolderOpen, visible: (c) => voitDomaine(c, 'dossier'), module: 'M02' },
  // Finances (lot L2-C) : recettes, créances, paiements, caisses, rapprochements, anomalies, catalogue.
  { id: 'finances', libelle: 'Finances', href: '/recettes', icone: Coins, visible: (c) => voitDomaine(c, 'recette') || voitDomaine(c, 'caisse'), module: 'M07' },
  // Budget et chaîne de la dépense (lot L4-B, M06) : domaines budget et depense.
  { id: 'budget', libelle: 'Budget', href: '/budget', icone: Landmark, visible: (c) => voitDomaine(c, 'budget') || voitDomaine(c, 'depense'), module: 'M06' },
  { id: 'territoire', libelle: 'Territoire', href: '/territoire', icone: IconeCarte, visible: horsPlateforme },
  // Marchés municipaux et domaine public (lot L2) : visibles avec un droit sur l'un des deux domaines.
  { id: 'marches', libelle: 'Marchés', href: '/marches', icone: Store, visible: (c) => voitDomaine(c, 'marche_municipal') || voitDomaine(c, 'domaine_public') },
  // Super administrateur de la plateforme (D-080) : catalogue et activation globale des modules.
  { id: 'plateforme', libelle: 'Plateforme', href: '/plateforme/modules', icone: LayoutGrid, visible: (c) => c?.appartenance_active?.institution === 'plateforme' },
  // Interventions (lot L3-A) : signalements, missions, équipes, conflits de synchronisation (module M12).
  { id: 'interventions', libelle: 'Interventions', href: '/interventions', icone: Wrench, visible: (c) => voitDomaine(c, 'intervention'), module: 'M12' },
  // Lot L3-B : propreté (M13, domaine dechets) et contrôles (M23, domaine inspection), dans le menu Plus.
  { id: 'proprete', libelle: 'Propreté', href: '/proprete', icone: Recycle, visible: (c) => voitDomaine(c, 'dechets'), module: 'M13', secondaire: true },
  { id: 'controles', libelle: 'Contrôles', href: '/inspections', icone: ClipboardCheck, visible: (c) => voitDomaine(c, 'inspection'), module: 'M23', secondaire: true },
  // Application terrain (lot L3-C) : agents de terrain et responsables techniques ; zone /terrain hors de la coquille.
  { id: 'terrain', libelle: 'Terrain', href: '/terrain', icone: Smartphone, visible: (c) => rolesActifs(c).some((r) => r === 'agent_terrain' || r === 'responsable_technique') },
  // Conseil apparaîtra avec le lot L5.
  { id: 'documents', libelle: 'Documents', href: '/documents', icone: FileText, visible: (c) => voitDomaine(c, 'document'), module: 'M03', secondaire: true },
  { id: 'demarches', libelle: 'Démarches', href: '/demarches', icone: FileCog, visible: (c) => voitDomaine(c, 'demarche_config') || voitDomaine(c, 'dossier'), module: 'M05', secondaire: true },
  { id: 'rendez-vous', libelle: 'Rendez-vous', href: '/rendez-vous', icone: CalendarDays, visible: (c) => voitDomaine(c, 'dossier'), module: 'M05', secondaire: true },
  { id: 'reclamations', libelle: 'Réclamations', href: '/reclamations', icone: MessageSquareWarning, visible: (c) => voitDomaine(c, 'reclamation') || voitDomaine(c, 'reclamation_sensible'), module: 'M22', secondaire: true },
  { id: 'recherche', libelle: 'Recherche', href: '/recherche', icone: Search, visible: horsPlateforme, secondaire: true },
  { id: 'utilisateurs', libelle: 'Utilisateurs', href: '/administration/utilisateurs', icone: Users, visible: (c) => peut(c, 'utilisateurs', 'voir'), secondaire: true },
  { id: 'roles', libelle: 'Rôles', href: '/administration/roles', icone: ShieldCheck, visible: (c) => peut(c, 'utilisateurs', 'voir'), secondaire: true },
  {
    id: 'habilitations',
    libelle: 'Habilitations',
    href: '/administration/habilitations',
    icone: KeyRound,
    visible: (c) => peut(c, 'utilisateurs', 'administrer') || peut(c, 'utilisateurs', 'valider'),
    secondaire: true,
  },
  { id: 'delegations', libelle: 'Délégations', href: '/administration/delegations', icone: Handshake, visible: (c) => Boolean(c?.appartenance_active) && horsPlateforme(c), secondaire: true },
  { id: 'parametres', libelle: 'Paramètres', href: '/administration/parametres', icone: Settings, visible: (c) => peut(c, 'parametres', 'voir'), secondaire: true },
  { id: 'modules', libelle: 'Modules', href: '/administration/modules', icone: LayoutGrid, visible: (c) => peut(c, 'parametres', 'voir'), secondaire: true },
  { id: 'historique', libelle: 'Historique', href: '/administration/historique', icone: History, visible: (c) => peut(c, 'audit', 'voir'), secondaire: true },
  { id: 'preferences', libelle: 'Préférences', href: '/preferences', icone: SlidersHorizontal, visible: () => true, secondaire: true },
];

export interface Navigation {
  principales: EntreeNavigation[];
  secondaires: EntreeNavigation[];
}

export function entreesNavigation(
  contexte: ContexteActif | null,
  modules: ModuleEtat[] | undefined,
  badges: Record<string, { nombre: number; libelle: string } | undefined> = {},
): Navigation {
  // Liste des modules (administrateurs) ou, pour tout agent, les modules actifs annoncés par GET /contexte.
  const annonces = contexte?.appartenance_active?.modules_actifs;
  const actifs = modules ? new Set(modules.filter((m) => m.actif).map((m) => m.module)) : annonces ? new Set(annonces) : null;
  const raccordees = new Set(['couts-reseau','pilotage-reseaux','notifications-reseau','equipes-reseau','pilotage', 'incidents-reseau', 'signalements-reseau', 'missions-reseau', 'stocks-reseau', 'patrimoine-reseau', 'maintenance-reseau', 'utilisateurs', 'roles', 'habilitations', 'delegations', 'historique', 'preferences']);
  raccordees.add('investissements-reseau');
  raccordees.add('instruction-investissements');
  raccordees.add('controles-regulation');
  raccordees.add('mandats-institutionnels');
  raccordees.add('communication-reseau');
  raccordees.add('terrain-reseau');
  raccordees.add('reclamations-reseau');
  const administrationLocale = new Set(['utilisateurs', 'roles', 'habilitations', 'delegations']);
  const visibles = DEFINITIONS.filter((d) => raccordees.has(d.id) && d.visible(contexte) && !(contexte?.appartenance_active?.institution && administrationLocale.has(d.id)) && (!d.module || !actifs || actifs.has(d.module)));
  const vers = ({ visible: _v, module: _m, secondaire: _s, ...e }: DefinitionEntree): EntreeNavigation => ({
    ...e,
    badge: badges[e.id]?.nombre ?? null,
    libelleBadge: badges[e.id]?.libelle,
  });
  return { principales: visibles.filter((d) => !d.secondaire).map(vers), secondaires: visibles.filter((d) => d.secondaire).map(vers) };
}

const PRIORITE_RAPIDES = ['pilotage', 'incidents-reseau', 'signalements-reseau', 'missions-reseau', 'utilisateurs', 'preferences'];

/** Quatre entrées de la navigation basse (téléphone). */
export function entreesRapides(navigation: Navigation): string[] {
  const ids = new Set([...navigation.principales, ...navigation.secondaires].map((e) => e.id));
  return PRIORITE_RAPIDES.filter((id) => ids.has(id)).slice(0, 4);
}

/** Entrée active d'après le chemin. */
export function entreeActive(chemin: string): string {
  if (chemin === '/' || chemin.startsWith('/tableau-de-bord')) return 'pilotage';
  const correspondance = DEFINITIONS.filter((d) => d.href !== '/').find((d) => chemin === d.href || chemin.startsWith(`${d.href}/`));
  return correspondance?.id ?? '';
}
